import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield, CheckCircle, Building2, Users, GraduationCap,
  FileCheck, Download, Phone, Mail, ArrowRight,
  Target, BarChart3, Clock, BookOpen, Star
} from "lucide-react";

const TexasSchools = () => {
  const [activeTab, setActiveTab] = useState("checklist");

  const complianceChecklist = [
    {
      category: "Governance + 'Rules of the Road'",
      items: [
        { item: "AI lead designated (instruction + tech + legal/privacy)", status: "supported", note: "District admin role with oversight capabilities" },
        { item: "AI acceptable-use policy alignment", status: "supported", note: "Configurable usage policies per district" },
        { item: "Clear AI use definitions for instruction", status: "compliant", note: "AI assists learning, never replaces teacher interaction" },
        { item: "Academic integrity language", status: "compliant", note: "All AI assistance is logged and transparent" }
      ]
    },
    {
      category: "Texas Student Privacy Act (Ch. 32)",
      items: [
        { item: "Operator acting for school purpose", status: "compliant", note: "Sole purpose is educational test preparation" },
        { item: "Covered information protection", status: "compliant", note: "All student data encrypted, no secondary use" },
        { item: "No unauthorized disclosure/sale", status: "compliant", note: "Zero data sharing with third parties" },
        { item: "Reasonable security measures", status: "compliant", note: "AES-256 encryption, TLS 1.3, audit logging" }
      ]
    },
    {
      category: "TRAIGA (HB 149) - Effective Jan 2026",
      items: [
        { item: "No prohibited AI uses", status: "compliant", note: "AI is assistive only, not decision-making" },
        { item: "Required disclosures in place", status: "compliant", note: "Clear AI disclosure in UI and terms" },
        { item: "Guardrails implemented", status: "compliant", note: "PII anonymization, human oversight, audit trails" }
      ]
    },
    {
      category: "TDPSA (July 2024)",
      items: [
        { item: "Privacy program alignment", status: "compliant", note: "Full transparency, data portability, deletion rights" },
        { item: "Consumer rights supported", status: "compliant", note: "Export data, request deletion via admin panel" }
      ]
    },
    {
      category: "Procurement + Vendor Vetting",
      items: [
        { item: "Data privacy agreement (Ch. 32)", status: "ready", note: "Template DPA available for districts" },
        { item: "Data ownership clarity", status: "compliant", note: "District owns all student data" },
        { item: "Retention/deletion timelines", status: "compliant", note: "Configurable retention, instant deletion" },
        { item: "Breach notice window", status: "compliant", note: "72-hour notification commitment" },
        { item: "No ad-targeting", status: "compliant", note: "Zero advertising, zero profiling" },
        { item: "No training on student data", status: "compliant", note: "Student data never used for AI training" },
        { item: "Security certifications", status: "in_progress", note: "SOC 2 Type II in progress" }
      ]
    },
    {
      category: "Data Handling Rules",
      items: [
        { item: "No student PII in AI prompts", status: "compliant", note: "Automatic PII detection and anonymization" },
        { item: "District-managed accounts (SSO)", status: "supported", note: "SAML 2.0 SSO integration available" },
        { item: "Chat history controls", status: "compliant", note: "Configurable retention, admin deletion" },
        { item: "Model training opt-out", status: "compliant", note: "Always opted out, contractually guaranteed" }
      ]
    },
    {
      category: "Classroom Implementation",
      items: [
        { item: "Allowed student uses defined", status: "compliant", note: "Practice, tutoring, feedback on learning" },
        { item: "Student disclosure rules", status: "compliant", note: "AI assistance clearly labeled in UI" },
        { item: "Verification routines", status: "supported", note: "Progress tracking, mastery verification" }
      ]
    },
    {
      category: "Special Education + Accessibility",
      items: [
        { item: "AI supports (not replaces) SPED", status: "compliant", note: "Assistive tool only, IEP decisions remain human" },
        { item: "Accessibility features", status: "compliant", note: "Screen reader support, Emergent Bilingual scaffolding" }
      ]
    },
    {
      category: "Staff Training + Monitoring",
      items: [
        { item: "Training materials available", status: "supported", note: "Teacher onboarding and safe prompting guides" },
        { item: "Tool approval controls", status: "supported", note: "Admin approval for teacher accounts" },
        { item: "Usage monitoring", status: "compliant", note: "Full audit logs, usage analytics" }
      ]
    }
  ];

  const pricingTiers = [
    {
      name: "Teacher Pack",
      price: "$99",
      period: "/year",
      description: "For individual classrooms",
      features: [
        "Up to 150 students",
        "Class roster management",
        "Assignment creation",
        "Progress analytics",
        "At-risk student alerts",
        "Reteach pack generator"
      ],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Campus Pack",
      price: "$499",
      period: "/year",
      description: "For entire schools",
      features: [
        "Unlimited students",
        "Unlimited teachers",
        "School-wide analytics",
        "SSO integration",
        "Priority support",
        "Custom onboarding"
      ],
      cta: "Contact Sales",
      popular: true
    },
    {
      name: "District Pack",
      price: "Custom",
      period: "",
      description: "For school districts",
      features: [
        "Multi-school management",
        "District-wide analytics",
        "Curriculum pushes",
        "Data privacy agreement",
        "Dedicated account manager",
        "SLA guarantees"
      ],
      cta: "Request Quote",
      popular: false
    }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case "compliant":
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">✓ Compliant</span>;
      case "supported":
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">✓ Supported</span>;
      case "ready":
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">✓ Ready</span>;
      case "in_progress":
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full">In Progress</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-xl text-slate-800">Clear Path Testing Solutions</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/compliance" className="text-slate-600 hover:text-indigo-600 font-medium">
              Compliance
            </Link>
            <Link 
              to="/contact" 
              className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/f/f7/Flag_of_Texas.svg" 
              alt="Texas Flag" 
              className="w-8 h-5 rounded"
            />
            <span className="text-blue-200 font-medium">Built for Texas Schools</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mb-4">
            Texas K-12 AI Compliance Package
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Clear Path Testing Solutions meets every requirement on the Texas K-12 School AI Checklist. 
            Ready for your district's procurement process.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => setActiveTab("checklist")}
              className="px-6 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-100 transition-colors flex items-center gap-2"
            >
              <FileCheck className="w-5 h-5" />
              View Compliance Checklist
            </button>
            <button 
              onClick={() => setActiveTab("pricing")}
              className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-500 transition-colors"
            >
              View District Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 px-4 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-8 text-center">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-medium text-slate-700">Texas Student Privacy Act</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-medium text-slate-700">TRAIGA Ready (Jan 2026)</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-medium text-slate-700">FERPA Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="font-medium text-slate-700">COPPA Compliant</span>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-4 px-4 bg-slate-100 border-b border-slate-200">
        <div className="max-w-4xl mx-auto flex gap-4">
          <button
            onClick={() => setActiveTab("checklist")}
            className={`px-6 py-3 rounded-xl font-bold transition-colors ${
              activeTab === "checklist" 
                ? "bg-indigo-600 text-white" 
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Compliance Checklist
          </button>
          <button
            onClick={() => setActiveTab("pricing")}
            className={`px-6 py-3 rounded-xl font-bold transition-colors ${
              activeTab === "pricing" 
                ? "bg-indigo-600 text-white" 
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            District Pricing
          </button>
          <button
            onClick={() => setActiveTab("features")}
            className={`px-6 py-3 rounded-xl font-bold transition-colors ${
              activeTab === "features" 
                ? "bg-indigo-600 text-white" 
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Teacher Features
          </button>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Checklist Tab */}
          {activeTab === "checklist" && (
            <div>
              <div className="mb-8 text-center">
                <h2 className="font-heading font-bold text-2xl text-slate-800 mb-2">
                  Texas K-12 School AI Checklist
                </h2>
                <p className="text-slate-600">
                  Complete compliance mapping for district/campus procurement
                </p>
              </div>

              <div className="space-y-6">
                {complianceChecklist.map((section, i) => (
                  <div key={i} className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden">
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                      <h3 className="font-heading font-bold text-lg text-slate-800">{section.category}</h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {section.items.map((item, j) => (
                        <div key={j} className="px-6 py-4 flex items-start gap-4">
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{item.item}</p>
                            <p className="text-sm text-slate-500 mt-1">{item.note}</p>
                          </div>
                          {getStatusBadge(item.status)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Download CTA */}
              <div className="mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center">
                <h3 className="font-heading font-bold text-2xl mb-2">Need a PDF for Your Committee?</h3>
                <p className="text-indigo-100 mb-6">
                  Download our complete compliance documentation package for your district's review process.
                </p>
                <button className="px-6 py-3 bg-white text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition-colors flex items-center gap-2 mx-auto">
                  <Download className="w-5 h-5" />
                  Download Compliance Package (PDF)
                </button>
              </div>
            </div>
          )}

          {/* Pricing Tab */}
          {activeTab === "pricing" && (
            <div>
              <div className="mb-8 text-center">
                <h2 className="font-heading font-bold text-2xl text-slate-800 mb-2">
                  Educator & District Pricing
                </h2>
                <p className="text-slate-600">
                  Flexible plans for classrooms, campuses, and districts
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {pricingTiers.map((tier, i) => (
                  <div 
                    key={i} 
                    className={`bg-white rounded-2xl border-2 overflow-hidden ${
                      tier.popular ? "border-indigo-400 shadow-lg" : "border-slate-200"
                    }`}
                  >
                    {tier.popular && (
                      <div className="bg-indigo-600 text-white text-center py-2 text-sm font-bold">
                        Most Popular
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-heading font-bold text-xl text-slate-800">{tier.name}</h3>
                      <p className="text-slate-500 text-sm mb-4">{tier.description}</p>
                      <div className="mb-6">
                        <span className="text-4xl font-black text-slate-800">{tier.price}</span>
                        <span className="text-slate-500">{tier.period}</span>
                      </div>
                      <ul className="space-y-3 mb-6">
                        {tier.features.map((feature, j) => (
                          <li key={j} className="flex items-center gap-2 text-slate-600">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/contact"
                        className={`block text-center py-3 rounded-xl font-bold transition-colors ${
                          tier.popular 
                            ? "bg-indigo-600 text-white hover:bg-indigo-700" 
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        {tier.cta}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Volume Discounts */}
              <div className="mt-8 bg-slate-100 rounded-2xl p-6 text-center">
                <p className="text-slate-600">
                  <strong>Volume discounts available</strong> for multi-year agreements and large districts. 
                  <Link to="/contact" className="text-indigo-600 hover:underline ml-1">Contact us</Link> for a custom quote.
                </p>
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === "features" && (
            <div>
              <div className="mb-8 text-center">
                <h2 className="font-heading font-bold text-2xl text-slate-800 mb-2">
                  Teacher Dashboard Features
                </h2>
                <p className="text-slate-600">
                  Everything educators need to support student success
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Class Roster Management</h3>
                  <p className="text-slate-600 text-sm">
                    Add students by email, view progress at a glance, identify who needs help.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">At-Risk Student Alerts</h3>
                  <p className="text-slate-600 text-sm">
                    Automatically flag students below 60% accuracy or inactive 5+ days.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Assignment Builder</h3>
                  <p className="text-slate-600 text-sm">
                    Create targeted assignments by test, topic, and question count with due dates.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Class Analytics</h3>
                  <p className="text-slate-600 text-sm">
                    See weak topics, struggle rates, completion rates across your class.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Reteach Pack Generator</h3>
                  <p className="text-slate-600 text-sm">
                    AI-generated remediation question sets for struggling students.
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 border-2 border-slate-200">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-800 mb-2">Time-on-Task Tracking</h3>
                  <p className="text-slate-600 text-sm">
                    See exactly how much time each student spends practicing.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
            Schedule a demo with our team or request a formal quote for your district.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="mailto:districts@clearpathtest.com" 
              className="flex items-center gap-2 text-slate-300 hover:text-white"
            >
              <Mail className="w-5 h-5" />
              districts@clearpathtest.com
            </a>
            <a 
              href="tel:+18005551234" 
              className="flex items-center gap-2 text-slate-300 hover:text-white"
            >
              <Phone className="w-5 h-5" />
              1-800-555-1234
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TexasSchools;
